import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  getWizardPendingRetainers, skipRetainerPeriod, wizardCreateInvoices,
  getBillingEntities,
} from '../api';
import { useOrgSettings } from '../contexts/OrgSettingsContext';

interface Props {
  onClose: () => void;
  onComplete: () => void;
}

interface ClientGstin { id: number; gstin?: string; stateCode?: string; isPrimary?: boolean; }
interface RetainerScheduleLite {
  amount: number | string;
  billingEntityId: number | null;
  clientGstinId: number | null;
  autoIncludeOpe: boolean;
  description: string | null;
}
interface OpeExpense { id: number; description: string; amount: number | string; date: string; category?: string; }
interface InstanceTask {
  id: number;
  taskId: string;
  taskName: string;
  billedAmount: number | null;
  clientId: number;
  client: { id: number; clientCode: string; clientName: string; gstins: ClientGstin[] };
  category?: { hsnSacCode?: string };
  billingEntity?: { id: number; name: string; stateCode?: string };
  expenses?: OpeExpense[];       // unbilled OPE available to attach to this task's invoice
}
// One pending retainer invoice: a (schedule, period) that hasn't been billed or
// skipped — or, when opeOnly, a task carrying unbilled OPE with no pending
// period left to attach it to. The latter has no schedule and no period: it
// bills the expenses on their own so they stop being stranded.
interface PendingInstance {
  key: string;                 // `${scheduleId}:${periodStartISO}`, or `ope:${taskId}`
  scheduleId: number | null;
  periodStart: string | null;  // ISO yyyy-mm-dd; null on an OPE-only row
  periodLabel: string;         // e.g. "Jun 2026", "Q1 FY 2026-27", "Out-of-pocket only"
  frequency: 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'YEARLY' | null;
  opeOnly?: boolean;
  task: InstanceTask;
  retainerSchedule: RetainerScheduleLite;
}

interface BillingEntity { id: number; name: string; stateCode?: string; }

interface InvoiceRow {
  key: string;
  taskId: number;
  scheduleId: number | null;
  periodStart: string | null;
  periodLabel: string;
  opeOnly: boolean;
  selected: boolean;
  description: string;
  amount: number;
  taxType: 'CGST_SGST' | 'IGST' | 'NONE';
  billingEntityId: number | '';
  clientGstinId: number | '';
  hsnSacCode: string;
  notes: string;
  autoIncludeOpe: boolean;      // server auto-includes all unbilled OPE when true
}

// FY start year → "FY 2026-27"
const fyLabel = (fyStart: number) => `FY ${fyStart}-${String((fyStart + 1) % 100).padStart(2, '0')}`;

const MonthlyInvoiceWizard: React.FC<Props> = ({ onClose, onComplete }) => {
  const { settings: orgSettings } = useOrgSettings();
  const today = new Date();
  const currentFyStart = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  // 3 = per-invoice OPE picker (only entered when at least one selected invoice has OPE to review), 4 = Done.
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Step 1 — financial year to bill for, and an optional default billing entity.
  const [fyStart, setFyStart] = useState(currentFyStart);
  const [billingEntityId, setBillingEntityId] = useState<number | ''>('');
  // Invoices are always dated today; due date derives from org payment terms.
  const [defaultInvoiceDate] = useState(format(today, 'yyyy-MM-dd'));

  const [billingEntities, setBillingEntities] = useState<BillingEntity[]>([]);

  // Step 2
  const [search, setSearch] = useState('');
  const [instances, setInstances] = useState<PendingInstance[]>([]);
  const [rows, setRows] = useState<Record<string, InvoiceRow>>({});
  const [loading, setLoading] = useState(false);

  // Step 3 — per-invoice OPE picker: the user's explicit choice per expense id —
  // the row key it was put on, or null when deliberately left unbilled. An
  // expense with no entry follows the default (see opeOwnerOf).
  const [opeOwner, setOpeOwner] = useState<Record<number, string | null>>({});
  const [opeIndex, setOpeIndex] = useState(0);

  // Step 4
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    getBillingEntities().then((be) => setBillingEntities(be.data || [])).catch(() => {});
  }, []);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await getWizardPendingRetainers(fyStart);
      const list: PendingInstance[] = res.data.instances || [];
      setInstances(list);
      // A reloaded list starts from fresh OPE defaults, not an earlier pass's ticks.
      setOpeOwner({});

      const newRows: Record<string, InvoiceRow> = {};
      const fallbackBe = (billingEntityId || '') as number | '';
      for (const inst of list) {
        const t = inst.task;
        const sched = inst.retainerSchedule;
        const primaryGstin = (t.client.gstins || []).find((g) => g.isPrimary) || t.client.gstins?.[0];
        // An OPE-only row bills expenses alone, so its service amount is 0 by
        // definition — never fall back to the task's billedAmount there.
        const resolvedAmount = inst.opeOnly
          ? 0
          : (sched && sched.amount != null && sched.amount !== ''
              ? Number(sched.amount)
              : (Number(t.billedAmount) || 0));
        const resolvedGstinId = sched?.clientGstinId ?? primaryGstin?.id ?? '';
        const resolvedBeId = sched?.billingEntityId ?? t.billingEntity?.id ?? fallbackBe;
        // Description: the schedule's own text if set, else the org-wide
        // default template (Settings → Invoicing). {taskName}/{periodLabel}
        // are substituted when present; otherwise the period is appended
        // after a dash (legacy custom descriptions with no placeholders).
        const descTpl = sched?.description?.trim() || orgSettings.invoicing.wizardDefaultDescriptionTemplate || t.taskName;
        const resolvedDesc = inst.opeOnly
          // No service line on an OPE-only invoice, so this text is never
          // printed; it only labels the row while reviewing.
          ? `Out-of-pocket expenses - ${t.taskName}`
          : (/\{periodLabel\}|\{taskName\}/.test(descTpl)
              ? descTpl.replace(/\{periodLabel\}/g, inst.periodLabel).replace(/\{taskName\}/g, t.taskName)
              : `${descTpl} - ${inst.periodLabel}`);

        const beForTax = billingEntities.find((b) => b.id === Number(resolvedBeId)) || t.billingEntity;
        const gstinForTax = (t.client.gstins || []).find((g) => g.id === Number(resolvedGstinId)) || primaryGstin;
        const isIntraState = beForTax?.stateCode && gstinForTax?.stateCode && beForTax.stateCode === gstinForTax.stateCode;

        newRows[inst.key] = {
          key: inst.key,
          taskId: t.id,
          scheduleId: inst.scheduleId,
          periodStart: inst.periodStart,
          periodLabel: inst.periodLabel,
          opeOnly: !!inst.opeOnly,
          // A client with no GSTIN can't be invoiced — leave it unselected
          // rather than letting it through with clientGstinId coerced to 0/NaN.
          // OPE-only rows start UNticked on purpose: they are a backlog the
          // wizard surfaces so it can be cleared deliberately, task by task,
          // not a batch to be created by clicking through. A retainer period
          // is due and stays ticked as before.
          selected: !inst.opeOnly && resolvedAmount > 0 && (t.client.gstins?.length ?? 0) > 0,
          description: resolvedDesc,
          amount: resolvedAmount,
          taxType: isIntraState ? 'CGST_SGST' : 'IGST',
          billingEntityId: resolvedBeId || '',
          clientGstinId: resolvedGstinId || '',
          hsnSacCode: t.category?.hsnSacCode || '',
          notes: '',
          // Default from Settings → Invoicing (usually ticked) — the OPE step
          // (if this task has unbilled expenses) lets the user drop specific
          // ones before the invoice is created.
          autoIncludeOpe: orgSettings.invoicing.wizardOpeDefaultOn !== false,
        };
      }
      setRows(newRows);
      setStep(2);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to load pending retainers');
    } finally { setLoading(false); }
  };

  const filteredInstances = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return instances;
    return instances.filter((i) =>
      i.task.taskId.toLowerCase().includes(q) ||
      (i.task.taskName || '').toLowerCase().includes(q) ||
      i.task.client.clientCode.toLowerCase().includes(q) ||
      (i.task.client.clientName || '').toLowerCase().includes(q) ||
      i.periodLabel.toLowerCase().includes(q),
    );
  }, [instances, search]);

  const selectedRows = useMemo(() =>
    instances.filter((i) => rows[i.key]?.selected).map((i) => rows[i.key]),
    [instances, rows]);

  const totalAmount = useMemo(() =>
    selectedRows.reduce((s, r) => s + (Number(r.amount) || 0), 0), [selectedRows]);

  // Selected invoices whose task has unbilled OPE and whose OPE checkbox is
  // ticked — these get their own review page before invoices are created.
  const opeQueue = useMemo(() =>
    instances.filter((i) => rows[i.key]?.selected && rows[i.key]?.autoIncludeOpe && (i.task.expenses?.length ?? 0) > 0),
    [instances, rows]);

  // A task's unbilled expenses are shared by all its rows (several periods, or
  // several schedules on different GSTINs), and an expense can be billed once.
  // Each expense therefore has ONE owner row, worked out fresh on every render:
  // the row the user put it on, provided that row is still being billed with
  // its OPE box ticked; otherwise the first such row of the task. An expense is
  // left off the batch only when the user unticks it (owner null).
  //
  // This replaced per-row tick sets seeded once. Those let the ticks sit on a
  // row that was later unticked on Step 2, while its sibling rows showed the
  // same expenses unticked — the invoices were then created with no OPE and
  // nothing said so (KBPL T0025, 01-Oct-26).
  const opeOwnerOf = useMemo(() => {
    const queued = new Set(opeQueue.map((i) => i.key));
    const firstRowOfTask = new Map<number, string>();
    for (const i of opeQueue) if (!firstRowOfTask.has(i.task.id)) firstRowOfTask.set(i.task.id, i.key);
    return (taskId: number, expenseId: number): string | null => {
      const chosen = opeOwner[expenseId];
      if (chosen === null) return null;
      if (chosen && queued.has(chosen)) return chosen;
      return firstRowOfTask.get(taskId) ?? null;
    };
  }, [opeQueue, opeOwner]);

  const opeIdsFor = (inst: PendingInstance) =>
    (inst.task.expenses || []).filter((e) => opeOwnerOf(inst.task.id, e.id) === inst.key).map((e) => e.id);

  const setOpeOwners = (expenseIds: number[], owner: string | null) =>
    setOpeOwner((prev) => {
      const next = { ...prev };
      for (const id of expenseIds) next[id] = owner;
      return next;
    });

  // Unbilled OPE on tasks being invoiced that will not ride on any invoice in
  // this batch — unticked on the picker, or every row's OPE box unticked.
  const opeLeftBehind = useMemo(() => {
    const seen = new Set<number>();
    const out: Array<{ label: string; count: number; total: number }> = [];
    for (const i of instances) {
      if (!rows[i.key]?.selected || seen.has(i.task.id)) continue;
      seen.add(i.task.id);
      const left = (i.task.expenses || []).filter((e) => opeOwnerOf(i.task.id, e.id) === null);
      if (left.length > 0) {
        out.push({
          label: `${i.task.taskId} ${i.task.client.clientName}`,
          count: left.length,
          total: left.reduce((s, e) => s + Number(e.amount), 0),
        });
      }
    }
    return out;
  }, [instances, rows, opeOwnerOf]);

  const handleRowUpdate = (key: string, patch: Partial<InvoiceRow>) => {
    setRows((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleSelectAllFiltered = (sel: boolean) => {
    setRows((prev) => {
      const out = { ...prev };
      for (const i of filteredInstances) {
        if (!out[i.key]) continue;
        // Never force-select a row whose client has no GSTIN — it can't be invoiced.
        if (sel && (i.task.client.gstins?.length ?? 0) === 0) continue;
        out[i.key] = { ...out[i.key], selected: sel };
      }
      return out;
    });
  };

  // Ignore a pending period — records a skip so it never reappears.
  // A skip is keyed by (schedule, period), so an OPE-only row has nothing to
  // record against: untick it instead, and it simply is not billed this run.
  const handleIgnore = async (inst: PendingInstance) => {
    if (inst.opeOnly || inst.scheduleId == null || !inst.periodStart) return;
    if (!window.confirm(
      `Ignore ${inst.task.taskId} — ${inst.periodLabel}?\n\nThis period will be marked as not-to-be-billed and will stop appearing in the wizard. You can still bill it later via a manual invoice.`,
    )) return;
    try {
      await skipRetainerPeriod({ scheduleId: inst.scheduleId, periodStart: inst.periodStart });
      setInstances((prev) => prev.filter((i) => i.key !== inst.key));
      setRows((prev) => { const out = { ...prev }; delete out[inst.key]; return out; });
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to ignore this period');
    }
  };

  const handleCreate = async () => {
    // Say what is being left behind before the invoices go out. A confirm, not
    // a block: holding OPE back for a later invoice is legitimate.
    if (opeLeftBehind.length > 0) {
      const lines = opeLeftBehind
        .map((l) => `  • ${l.label} — ${l.count} expense${l.count > 1 ? 's' : ''}, ₹${l.total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`)
        .join('\n');
      if (!window.confirm(
        `These out-of-pocket expenses are NOT on any invoice in this batch:\n\n${lines}\n\n`
        + 'Press OK to create the invoices without them, or Cancel to go back and tick them.',
      )) return;
    }
    setCreating(true);
    setResult(null);
    try {
      const instByKey = new Map(instances.map((i) => [i.key, i]));
      const items = selectedRows.map((row) => {
        // Only the expenses this row owns on the OPE picker; a row with its
        // OPE box unticked sends none (no blind auto-include).
        const inst = instByKey.get(row.key);
        const expenseIds = row.autoIncludeOpe && inst ? opeIdsFor(inst) : [];
        return {
          taskId: row.taskId,
          retainerScheduleId: row.scheduleId,
          // An OPE-only invoice covers no retainer period, so it stamps none —
          // stamping one would mark that period billed and hide it from the
          // wizard for good.
          retainerPeriodStart: row.periodStart,
          opeOnly: row.opeOnly,
          description: row.description,
          amount: Number(row.amount),
          taxType: row.taxType,
          billingEntityId: row.billingEntityId ? Number(row.billingEntityId) : undefined,
          clientGstinId: Number(row.clientGstinId),
          hsnSacCode: row.hsnSacCode || undefined,
          notes: row.notes || undefined,
          autoIncludeOpe: false,
          expenseIds,
        };
      });
      const res = await wizardCreateInvoices({ invoiceDate: defaultInvoiceDate, defaultBillingEntityId: billingEntityId ? Number(billingEntityId) : undefined, items });
      setResult(res.data);
      setStep(4);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Creation failed');
    } finally { setCreating(false); }
  };

  // From Step 2: either drop straight into invoice creation (nothing to
  // review) or open the OPE picker on its first pending invoice.
  const handleReviewNext = () => {
    if (opeQueue.length === 0) { handleCreate(); return; }
    setOpeIndex(0);
    setStep(3);
  };

  const handleOpeNext = () => {
    if (opeIndex < opeQueue.length - 1) setOpeIndex((i) => i + 1);
    else handleCreate();
  };

  const handleOpeBack = () => {
    if (opeIndex > 0) setOpeIndex((i) => i - 1);
    else setStep(2);
  };

  const fyOptions = [currentFyStart + 1, currentFyStart, currentFyStart - 1, currentFyStart - 2];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">📅 Retainer Invoice Wizard</h2>
            <p className="text-xs text-gray-500 mt-0.5">Bill the pending retainer invoices for a financial year</p>
          </div>
          <button className="text-gray-400 hover:text-gray-600 text-2xl leading-none" onClick={onClose}>✕</button>
        </div>

        {/* Step indicator */}
        <div className="px-6 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-2 text-sm">
            {(opeQueue.length > 0
              ? ([[1, 'Select Year'], [2, 'Review Pending'], [3, 'Select OPE'], [4, 'Done']] as const)
              : ([[1, 'Select Year'], [2, 'Review Pending'], [4, 'Done']] as const)
            ).map(([s, lbl], idx) => (
              <React.Fragment key={s}>
                {idx > 0 && <div className="flex-1 h-px bg-gray-200" />}
                <div className={`flex items-center gap-1.5 ${step === s ? 'text-blue-700 font-semibold' : step > s ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${step === s ? 'bg-blue-600 text-white' : step > s ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>{step > s ? '✓' : idx + 1}</span>
                  {lbl}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="label">Financial Year *</label>
                <select className="input-field max-w-xs" value={fyStart} onChange={(e) => setFyStart(Number(e.target.value))}>
                  {fyOptions.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  The wizard lists every pending retainer invoice for this year — each active schedule's monthly/quarterly
                  periods up to the current one, excluding those already billed or ignored.
                </p>
              </div>

              <p className="text-xs text-gray-500">
                Invoices are dated <strong>today</strong>; the due date is set automatically from the default payment terms in Settings.
              </p>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Default Billing Entity (optional)</h4>
                <select className="input-field max-w-md" value={billingEntityId} onChange={(e) => setBillingEntityId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">— Use each schedule's / task's entity —</option>
                  {billingEntities.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">Fallback billing entity for any row that doesn't have one set.</p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                ℹ️ Each row is pre-filled from its retainer schedule (amount, GSTIN, billing entity, description with the period).
                Tick-confirm to bill, edit any field, or <em>Ignore</em> a period you don't want to bill. The <strong>OPE</strong> checkbox is
                ticked by default — if a task has unbilled out-of-pocket expenses, you'll get a chance to pick which ones to add before
                invoices are created. Set up schedules in the <strong>Retainers</strong> tab.
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-gray-800">
                  {fyLabel(fyStart)} — {instances.length} pending invoice{instances.length !== 1 ? 's' : ''}
                </h3>
                <div className="flex gap-2 items-center text-sm">
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search task / client / period…" className="input-field text-xs py-1 w-56" />
                  <button onClick={() => handleSelectAllFiltered(true)} className="text-xs text-blue-600 hover:underline">Select All</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={() => handleSelectAllFiltered(false)} className="text-xs text-gray-500 hover:underline">Deselect All</button>
                  <span className="ml-3 text-gray-700">
                    Selected: <b>{selectedRows.length}</b> · Total: <b className="text-blue-700">₹{totalAmount.toLocaleString('en-IN')}</b>
                  </span>
                </div>
              </div>

              {instances.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  No pending retainer invoices for {fyLabel(fyStart)}.<br />
                  Everything is either already billed, ignored, or not yet due.
                </div>
              ) : filteredInstances.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  No pending invoices match "<span className="font-mono">{search}</span>".
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-2 w-8 text-center">
                          <input type="checkbox"
                            checked={filteredInstances.length > 0 && filteredInstances.every((i) => rows[i.key]?.selected)}
                            onChange={(e) => handleSelectAllFiltered(e.target.checked)} />
                        </th>
                        <th className="px-2 py-2 text-left">Task</th>
                        <th className="px-2 py-2 text-left">Client</th>
                        <th className="px-2 py-2 text-left">Period</th>
                        <th className="px-2 py-2 text-left">Client GSTIN</th>
                        <th className="px-2 py-2 text-left">Description</th>
                        <th className="px-2 py-2 text-left">Billing Entity</th>
                        <th className="px-2 py-2 text-right">Amount (₹)</th>
                        <th className="px-2 py-2 text-center">Tax</th>
                        <th className="px-2 py-2 text-left">HSN/SAC</th>
                        <th className="px-2 py-2 text-center">OPE</th>
                        <th className="px-2 py-2 text-left">Notes</th>
                        <th className="px-2 py-2 text-center">Ignore</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInstances.map((inst) => {
                        const t = inst.task;
                        const row = rows[inst.key];
                        if (!row) return null;
                        return (
                          <tr key={inst.key} className={`border-t border-gray-100 ${row.selected ? '' : 'opacity-50'}`}>
                            <td className="px-2 py-1.5 text-center">
                              <input type="checkbox" checked={row.selected}
                                onChange={(e) => handleRowUpdate(inst.key, { selected: e.target.checked })} />
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="font-mono text-blue-600 font-medium">{t.taskId}</div>
                              <div className="text-gray-500 truncate max-w-[160px]" title={t.taskName}>{t.taskName}</div>
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="font-mono text-blue-600">{t.client.clientCode}</div>
                              <div className="text-gray-600 truncate max-w-[140px]" title={t.client.clientName}>{t.client.clientName}</div>
                            </td>
                            <td className="px-2 py-1.5 whitespace-nowrap font-medium text-gray-700">
                              {inst.opeOnly ? (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800"
                                  title="This task has unbilled out-of-pocket expenses but no retainer period due. Bill them on their own so they stop being carried forward.">
                                  Out-of-pocket only
                                </span>
                              ) : inst.periodLabel}
                            </td>
                            <td className="px-2 py-1.5">
                              {t.client.gstins && t.client.gstins.length > 0 ? (
                                <select className="input-field text-xs py-1 w-44 font-mono"
                                  value={row.clientGstinId}
                                  onChange={(e) => handleRowUpdate(inst.key, { clientGstinId: Number(e.target.value) })}>
                                  {t.client.gstins.map((g) => (
                                    <option key={g.id} value={g.id}>{g.gstin || `GSTIN #${g.id}`}{g.isPrimary ? ' ★' : ''}</option>
                                  ))}
                                </select>
                              ) : (
                                <span className="text-amber-600 text-[11px]">No GSTIN on client</span>
                              )}
                            </td>
                            <td className="px-2 py-1.5">
                              <input className="input-field text-xs py-1 w-56" value={row.description}
                                onChange={(e) => handleRowUpdate(inst.key, { description: e.target.value })}
                                placeholder="Invoice line description" />
                            </td>
                            <td className="px-2 py-1.5">
                              <select className="input-field text-xs py-1" value={row.billingEntityId}
                                onChange={(e) => handleRowUpdate(inst.key, { billingEntityId: e.target.value ? Number(e.target.value) : '' })}>
                                <option value="">{billingEntityId ? '(use batch default)' : '—'}</option>
                                {billingEntities.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <input type="number" step="0.01" className="input-field text-xs py-1 text-right w-24"
                                value={row.amount} onChange={(e) => handleRowUpdate(inst.key, { amount: Number(e.target.value) })} />
                            </td>
                            <td className="px-2 py-1.5">
                              <select className="input-field text-xs py-1" value={row.taxType}
                                onChange={(e) => handleRowUpdate(inst.key, { taxType: e.target.value as any })}>
                                <option value="CGST_SGST">CGST/SGST</option>
                                <option value="IGST">IGST</option>
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <input className="input-field text-xs py-1 w-20" value={row.hsnSacCode}
                                onChange={(e) => handleRowUpdate(inst.key, { hsnSacCode: e.target.value })} />
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              <label className="inline-flex items-center gap-1 cursor-pointer" title="Review this task's unbilled out-of-pocket expenses before creating the invoice">
                                <input type="checkbox" checked={row.autoIncludeOpe}
                                  onChange={(e) => handleRowUpdate(inst.key, { autoIncludeOpe: e.target.checked })} />
                                {(t.expenses?.length ?? 0) > 0 && <span className="text-[10px] text-gray-400">({t.expenses!.length})</span>}
                              </label>
                            </td>
                            <td className="px-2 py-1.5">
                              <input className="input-field text-xs py-1 w-40" value={row.notes}
                                onChange={(e) => handleRowUpdate(inst.key, { notes: e.target.value })}
                                placeholder="Optional notes" />
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              {/* A skip is recorded against (schedule, period);
                                  an OPE-only row has neither, so untick it. */}
                              {!inst.opeOnly && (
                                <button type="button" onClick={() => handleIgnore(inst)}
                                  title="Ignore this period — won't appear again"
                                  className="text-red-500 hover:text-red-700">✕</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — per-invoice OPE picker */}
          {step === 3 && opeQueue.length > 0 && (() => {
            const inst = opeQueue[Math.min(opeIndex, opeQueue.length - 1)];
            const row = rows[inst.key];
            const expenses = inst.task.expenses || [];
            const selected = new Set(opeIdsFor(inst));
            // Where an expense sits when it is on a sibling invoice of the same task.
            const siblingLabel = (expenseId: number) => {
              const owner = opeOwnerOf(inst.task.id, expenseId);
              if (!owner || owner === inst.key) return null;
              const sib = opeQueue.find((i) => i.key === owner);
              const g = sib && (sib.task.client.gstins || []).find((x) => x.id === Number(rows[sib.key]?.clientGstinId));
              return `on the invoice for ${g?.gstin || sib?.periodLabel || 'another row'}`;
            };
            const selectedTotal = expenses.filter((e) => selected.has(e.id)).reduce((s, e) => s + Number(e.amount), 0);
            return (
              <div className="space-y-4 max-w-3xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">
                      Invoice {opeIndex + 1} of {opeQueue.length} — {inst.task.taskId} · {inst.task.client.clientName} · {inst.periodLabel}
                    </h3>
                    {(() => {
                      const g = (inst.task.client.gstins || []).find((x) => x.id === Number(row?.clientGstinId));
                      return g ? (
                        <p className="text-xs text-gray-700 mt-0.5">
                          Invoice to be raised on GSTIN: <span className="font-mono font-semibold">{g.gstin || `#${g.id}`}</span>
                          {g.stateCode ? <span className="text-gray-500"> (state {g.stateCode})</span> : null}
                        </p>
                      ) : (
                        <p className="text-xs text-amber-600 mt-0.5">No GSTIN selected for this invoice</p>
                      );
                    })()}
                    <p className="text-xs text-gray-500 mt-0.5">
                      Pick which out-of-pocket expenses to recover on this invoice. An expense goes on one invoice only; anything not ticked on any invoice stays unbilled.
                    </p>
                  </div>
                  <div className="flex gap-2 text-xs">
                    {/* Select All moves every expense onto this invoice; Deselect
                        All only drops the ones this invoice holds. */}
                    <button className="text-blue-600 hover:underline"
                      onClick={() => setOpeOwners(expenses.map((e) => e.id), inst.key)}>Select All</button>
                    <span className="text-gray-300">|</span>
                    <button className="text-gray-500 hover:underline"
                      onClick={() => setOpeOwners(Array.from(selected), null)}>Deselect All</button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-2 w-8 text-center"></th>
                        <th className="px-2 py-2 text-left">Date</th>
                        <th className="px-2 py-2 text-left">Description</th>
                        <th className="px-2 py-2 text-left">Category</th>
                        <th className="px-2 py-2 text-right">Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((e) => (
                        <tr key={e.id} className={`border-t border-gray-100 ${selected.has(e.id) ? '' : 'opacity-50'}`}>
                          <td className="px-2 py-1.5 text-center">
                            <input type="checkbox" checked={selected.has(e.id)}
                              onChange={(ev) => setOpeOwners([e.id], ev.target.checked ? inst.key : null)} />
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap text-gray-600">{format(new Date(e.date), 'dd-MMM-yy')}</td>
                          <td className="px-2 py-1.5">
                            {e.description || '—'}
                            {siblingLabel(e.id) && <div className="text-[10px] text-indigo-600">{siblingLabel(e.id)}</div>}
                          </td>
                          <td className="px-2 py-1.5 text-gray-500">{e.category || '—'}</td>
                          <td className="px-2 py-1.5 text-right font-medium">{Number(e.amount).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 flex justify-between">
                  <span>Service amount: ₹{Number(row?.amount || 0).toLocaleString('en-IN')} + OPE selected: ₹{selectedTotal.toLocaleString('en-IN')}</span>
                  <span className="font-semibold">Invoice taxable total: ₹{(Number(row?.amount || 0) + selectedTotal).toLocaleString('en-IN')}</span>
                </div>
              </div>
            );
          })()}

          {/* STEP 4 */}
          {step === 4 && result && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-4xl mb-2">✅</div>
                <h3 className="text-lg font-bold text-green-800">Batch Creation Complete</h3>
                <p className="text-sm text-green-700 mt-1">
                  {result.created} invoice{result.created !== 1 ? 's' : ''} created successfully
                  {result.failed > 0 && `, ${result.failed} failed`}
                </p>
              </div>

              {result.succeeded?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Created Invoices ({result.succeeded.length})</h4>
                  <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr><th className="px-2 py-1 text-left">Task</th><th className="px-2 py-1 text-left">Invoice #</th></tr>
                      </thead>
                      <tbody>
                        {result.succeeded.map((s: any, i: number) => (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-2 py-1 font-mono text-blue-600">{s.taskCode}</td>
                            <td className="px-2 py-1 font-mono text-blue-700 font-medium">{s.invoiceNumber}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {result.failures?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-red-700 mb-2">Failures ({result.failures.length})</h4>
                  <div className="border border-red-200 rounded-lg max-h-40 overflow-y-auto bg-red-50">
                    <table className="w-full text-xs">
                      <tbody>
                        {result.failures.map((f: any, i: number) => (
                          <tr key={i} className="border-t border-red-100">
                            <td className="px-2 py-1 text-red-700">Task #{f.taskId}</td>
                            <td className="px-2 py-1 text-red-600">{f.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <div className="flex gap-2">
            {step === 2 && <button className="btn-secondary" onClick={() => setStep(1)}>← Back</button>}
            {step === 3 && <button className="btn-secondary" onClick={handleOpeBack}>← Back</button>}
            {step === 1 && (
              <button className="btn-primary" onClick={fetchPending} disabled={loading}>
                {loading ? 'Loading…' : 'Next: Review Pending →'}
              </button>
            )}
            {step === 2 && (
              <button className="btn-primary" onClick={handleReviewNext}
                disabled={creating || selectedRows.length === 0}>
                {creating ? 'Creating…' : opeQueue.length > 0
                  ? 'Next: Select OPE →'
                  : `Create ${selectedRows.length} Invoice${selectedRows.length !== 1 ? 's' : ''}`}
              </button>
            )}
            {step === 3 && (
              <button className="btn-primary" onClick={handleOpeNext} disabled={creating}>
                {creating ? 'Creating…' : opeIndex < opeQueue.length - 1
                  ? 'Next Invoice →'
                  : `Create ${selectedRows.length} Invoice${selectedRows.length !== 1 ? 's' : ''}`}
              </button>
            )}
            {step === 4 && (
              <button className="btn-primary" onClick={() => { onComplete(); onClose(); }}>Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyInvoiceWizard;
